/**
 * Shared basket store for web components.
 * Uses localStorage for persistence and CustomEvent/StorageEvent for
 * cross-component and cross-tab reactivity.
 *
 * Compatible with the widget app's Zustand basket store shape
 * (same localStorage key "baluu-basket").
 */

import { effectiveItemPrice, type BasketUnavailability } from "@obby/lib";
import { productSurcharge } from "@baluu/ui/lib/checkout";
import type { ParticipantPayloadEntry } from "@baluu/ui/components/checkout";
import {
  BASKET_STORAGE_KEY,
  BASKET_UPDATED_EVENT as SHARED_BASKET_UPDATED_EVENT,
  countBasketItems,
} from "@baluu/ui/lib/checkout/basket-surface";

const STORAGE_KEY = BASKET_STORAGE_KEY;
const BASKET_UPDATED_EVENT = SHARED_BASKET_UPDATED_EVENT;
const BASKET_ERROR_EVENT = "baluu:basket-error";

let _storageAvailable: boolean | null = null;

function isLocalStorageAvailable(): boolean {
  if (_storageAvailable !== null) return _storageAvailable;
  try {
    const testKey = "__baluu_storage_test__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    _storageAvailable = true;
  } catch {
    _storageAvailable = false;
  }
  return _storageAvailable;
}

function emitStorageError(operation: string, detail?: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(BASKET_ERROR_EVENT, {
      detail: {
        operation,
        message: detail || "localStorage is unavailable. Basket items may not persist.",
      },
    }),
  );
}

export type BasketItemType = "booking" | "product" | "giftcard" | "bundle";

export type TicketSelection = {
  ticketTypeId: string;
  ticketTypeName: string;
  quantity: number;
  price: number;
  /** Authoritative line total (e.g. from tiered pricing). If provided, overrides price × quantity. */
  lineTotal?: number;
  bookingPolicy?: any;
};

export type AddOnSelection = {
  addOnId: string;
  addOnName: string;
  quantity: number;
  price: number;
  groupId?: string | null;
  groupName?: string | null;
};

export type BasketItem = {
  id: string;
  type: BasketItemType;
  schoolId: string;
  schoolName: string;

  courseId?: string;
  courseName?: string;
  courseSlug?: string;
  sessionId?: string;
  sessionDate?: string;
  /** The LISTING's timezone, stamped when the item was built. The basket
   *  drawer has no school config to read one from, so it travels with the
   *  item -- see formatBookingWindow. */
  timezone?: string;
  sessionEndDate?: string;

  productId?: string;
  productName?: string;
  productSlug?: string;
  postageId?: string;
  /** Per-unit postage, pence. Display-only until validation stamps the
   *  server's own figure (see `postageTotal`). */
  postagePrice?: number;
  /** The postage option's name, for the summary row. */
  postageName?: string;
  /** Postage for the WHOLE line (unit × quantity), pence, as the server
   *  resolved it from the postage record — the figure the PaymentIntent adds.
   *  Stamped by validation; shared with the widget through localStorage. */
  postageTotal?: number;
  /** Gift-wrap fee for the line, pence (once, not per unit). Server-resolved
   *  like `postageTotal`. */
  giftingFee?: number;
  isGift?: boolean;
  giftingDetails?: {
    message: string;
    recipientName: string;
    /** Maps to `giftingDetails.from` on the ProductPurchase. */
    senderName?: string;
  };

  giftcardAmount?: number;
  recipientEmail?: string;
  recipientName?: string;
  senderName?: string;
  message?: string;

  bundleTemplateId?: string;
  bundleName?: string;
  bundleDescription?: string;
  bundleTotalSessions?: number;
  bundleExpiresIn?: number;
  bundleExpirationPeriodType?: string;

  ticketSelections?: TicketSelection[];
  addOns?: AddOnSelection[];
  bookingType?:
    | "regular"
    | "dropIn"
    | "trial"
    | "term"
    | "appointment"
    // Rentals have always reached this store; the union just never said so.
    | "rental";
  isOnline?: boolean;
  /**
   * Identity of the checkout pass that created this item — the server groups
   * basket items by it for ticket-rule validation and flat-price splitting.
   */
  checkoutGroupId?: string;
  /**
   * Waiting-list claim. Nothing on this surface sets it yet; declared so the
   * validate payload can name it, because the server uses it to exempt a
   * claimer's OWN offer hold from the capacity subtraction before their email
   * is known — precisely the anonymous case the drawer validates in. The
   * widget already sends it (useBasketSync). Left undeclared, it would be the
   * next field to go missing from the payload the way `appointmentData` did.
   */
  offerRef?: string;

  termData?: {
    termId: string;
    termName: string;
    isProRata: boolean;
    sessionsIncluded: number;
    startDate: string;
    endDate: string;
  };

  appointmentData?: {
    startTime: string;
    endTime: string;
    duration: number;
    /**
     * How many fraction-units the hire is for. RENTAL ONLY, and load-bearing:
     * ValidateBasketItems refuses a rental outright without it ("This rental
     * item is missing how long the hire is for"), and the overlay persists
     * that refusal onto the item, where it survives in localStorage.
     *
     * Declared because the handlers that fill this store forward
     * `appointmentData` as a whole object through `any`-typed props, so the
     * key travelled unseen by the compiler. Anyone normalising this object
     * field by field — which is exactly what CheckoutOverlay does for every
     * TOP-LEVEL field, and how `timezone` went missing once — would drop it
     * silently. The widget's own copy declares it
     * (apps/widget/src/stores/basket/types.ts).
     */
    fractions?: number;
  };

  guestDetails?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };

  /** Product items: the guests the product checkout collected (each one's
   *  marketing and terms ticks), forwarded to the purchase as the widget does. */
  checkoutGuestsInformation?: Array<{
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    marketingOptIn?: boolean;
    mandatoryTickBox?: boolean;
  }>;

  userInputs?: Array<{
    index: number;
    guestIndex: number | null;
    label: string;
    value: string | boolean | Date | null | number | string[];
  }>;

  /**
   * Attendees selected/added at the participants step (per item). Existing
   * participants carry only an id; inline ones carry answers. Shape shared
   * with the widget app via localStorage["baluu-basket"].
   */
  participants?: ParticipantPayloadEntry[];

  quantity: number;
  price: number;
  currency: string;
  imageUrl?: string;
  addedAt: string;

  isAvailable?: boolean;
  availabilityMessage?: string;
  /** Product items: server says the product ships (postage configured), so
   * checkout must collect a delivery address. Stamped by the validate poll. */
  requiresDeliveryAddress?: boolean;
  /**
   * Structured unavailability reason. Written by `applyValidationResults`
   * below and by the widget app's basket validate poll — we share
   * `localStorage["baluu-basket"]` with the widget, so the field has to mean
   * the same thing on both sides.
   */
  unavailability?: BasketUnavailability;
  priceChanged?: boolean;
  currentPrice?: number;
  // Deposit ticket (chargeType:"deposit") split, from basket validation. The
  // discount memo applies a code to the full contract (deposit + balance) and
  // charges only the deposit's now-share, matching the server.
  isDeposit?: boolean;
  depositAmount?: number; // pence charged now (whole item)
  remainingBalance?: number; // pence owed later (whole item)
  /** Per-ticket-line deposit split from the server validate (deposit items
   *  only): the line's deposit-due-now and full contract total, pence,
   *  quantity-scaled. `depositPence: null` = full-price line. */
  ticketDeposits?: {
    ticketTypeId: string;
    depositPence: number | null;
    lineTotalPence: number;
  }[];
  /** Booking items: the server-resolved add-on total inside the charged
   *  price, pence per unit. The discount memo prices it as its own line so a
   *  code discounts add-ons too (DEV-327). Absent = no add-ons. */
  addOnTotalPence?: number;
};

/**
 * One item's verdict from `POST /widget/basket/validate`.
 *
 * Declared here rather than imported: the widget keeps its own copy
 * (apps/widget/src/stores/basket/types.ts) and neither app depends on the
 * other. Only the fields this surface actually stamps are named.
 *
 * `currentPrice` is optional here though the server always sends it, so a
 * response from an older API can't be read as a price of `undefined`.
 */
export type BasketValidationResult = {
  itemId: string;
  available: boolean;
  message?: string;
  unavailability?: BasketUnavailability;
  currentPrice?: number;
  availableQuantity?: number;
  isDeposit?: boolean;
  depositAmount?: number;
  remainingBalance?: number;
  ticketDeposits?: {
    ticketTypeId: string;
    depositPence: number | null;
    lineTotalPence: number;
  }[];
  /** Booking items: add-on total inside `currentPrice`, pence (absent = none). */
  addOnTotalPence?: number;
  requiresDeliveryAddress?: boolean;
  /** Product items: the postage option the server resolved from `postageId`
   *  (school- and product-scoped). Absent = no postage is being charged. */
  postage?: { id: string; name: string; unitPrice: number; description?: string };
  /** Product items: `postage.unitPrice × quantity`, pence. */
  postageTotal?: number;
  /** Product items: gift-wrap fee for the line, pence (once, not per unit). */
  giftingFee?: number;
};

/**
 * The buyer's email, once any surface on this page has learned it.
 *
 * Load-bearing for availability, not a convenience. `ValidateBasketItems`
 * excludes the buyer's OWN reservation holds from the "others" sum only when
 * it is given an email — `buyerEmail ? { buyerEmail: { not: … } } : {}` for
 * seats, `excludeBuyerEmail` for resource pools. Ask anonymously and a buyer
 * who reached Pay, created holds and came back is measured against himself:
 * SESSION_SOLD_OUT, RESOURCE_POOL_SOLD_OUT or APPOINTMENT_SLOT_TAKEN, all hard
 * blockers, on a basket that is genuinely his. The rental-with-equipment case
 * is the one this branch exists for.
 *
 * That matters because the drawer has no email field of its own, and its
 * Checkout button is the only route to the overlay that does. So the overlay
 * publishes what it knows and the drawer asks the same question.
 *
 * In memory only, deliberately: this is an email address, and it buys nothing
 * once the page is gone — the holds it protects expire on their own TTL. A
 * reload therefore still asks anonymously.
 */
let _knownBuyerEmail: string | null = null;

function generateId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

type StoredState = {
  state: { items: BasketItem[] };
  version: number;
};

let _cachedRaw: string | null | undefined = undefined;
let _cachedItems: BasketItem[] = [];
const _cachedFiltered = new Map<string, BasketItem[]>();

/**
 * Everything a validate response stamps onto an item, and therefore everything
 * that is per-SESSION rather than part of the item the buyer chose.
 *
 * Kept as one list because the two things that need it must not diverge: the
 * page-load strip below, and `applyValidationResults`, which has to be able to
 * clear a field it previously set. It matches the widget's
 * `clearValidationState` (apps/widget/src/stores/basket/index.ts) field for
 * field, so an item handed between the two surfaces through the shared
 * `baluu-basket` key means the same thing on both.
 *
 * `requiresDeliveryAddress` is deliberately NOT here, and is not in the
 * widget's list either: it answers "does this product ship?", a property of
 * the product rather than a verdict about this session's availability.
 */
const VALIDATION_STAMPED_FIELDS = [
  "isAvailable",
  "availabilityMessage",
  "unavailability",
  "priceChanged",
  "currentPrice",
  "isDeposit",
  "depositAmount",
  "remainingBalance",
  "ticketDeposits",
  "addOnTotalPence",
] as const;

/** Returns the SAME array when there is nothing to clear, so callers can use
 *  identity to skip a pointless write + broadcast. */
function stripValidationState(items: BasketItem[]): BasketItem[] {
  const hasAny = items.some((item) =>
    VALIDATION_STAMPED_FIELDS.some((field) => item[field] !== undefined),
  );
  if (!hasAny) return items;
  return items.map((item) => {
    const next = { ...item };
    for (const field of VALIDATION_STAMPED_FIELDS) delete next[field];
    return next;
  });
}

/**
 * Whether this page load has read the basket yet. Availability verdicts are
 * per-SESSION, so the first read of a page load drops any it finds stored.
 *
 * They are written by the validate calls on this surface and read by
 * basket-drawer, which hard-blocks Checkout on `isAvailable === false`. A
 * verdict outliving the page load that produced it used to be a trap with no
 * way out: a buyer whose rental was refused before the embed sent
 * `appointmentData` at all reopens the drawer, is told "This rental item is
 * missing how long the hire is for", and cannot reach the checkout that would
 * clear it. `currentPrice` is in the same bag for a different reason —
 * `effectiveItemPrice` prefers it, so a figure from an expired pricing rule
 * is displayed as the basket total until something revalidates.
 *
 * Cleared in memory only, and only on that first read. NOT on every parse:
 * a cross-tab write lands here as a fresh parse (`raw !== _cachedRaw`), and
 * stripping that would wipe a verdict the other tab produced seconds ago.
 *
 * Note this is NOT the case the flag originally claimed to guard. A write from
 * THIS tab never re-parses at all — `writeItems` sets `_cachedRaw`, so the
 * next `readItems` short-circuits on it and returns the cached array.
 */
let _hasReadSinceLoad = false;

function readItems(): BasketItem[] {
  if (!isLocalStorageAvailable()) {
    return _cachedItems;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === _cachedRaw) return _cachedItems;
    _cachedRaw = raw;
    _cachedFiltered.clear();
    // Spent here, by the first read of the page load, whatever that read
    // finds - an empty basket included. Spending it at the first successful
    // PARSE instead meant a page that started with no basket never spent it,
    // so the next cross-tab update was stripped of a verdict seconds old.
    const isFirstReadOfPageLoad = !_hasReadSinceLoad;
    _hasReadSinceLoad = true;
    if (!raw) {
      _cachedItems = [];
      return _cachedItems;
    }
    const parsed: StoredState = JSON.parse(raw);
    const stored = parsed?.state?.items ?? [];
    _cachedItems = isFirstReadOfPageLoad ? stripValidationState(stored) : stored;
    return _cachedItems;
  } catch {
    _cachedItems = [];
    _cachedFiltered.clear();
    return _cachedItems;
  }
}

function writeItems(items: BasketItem[]): boolean {
  _cachedItems = items;
  _cachedFiltered.clear();

  if (!isLocalStorageAvailable()) {
    emitStorageError("write", "localStorage is unavailable. Your basket will not persist across page refreshes.");
    return false;
  }

  try {
    const stored: StoredState = { state: { items }, version: 0 };
    const raw = JSON.stringify(stored);
    localStorage.setItem(STORAGE_KEY, raw);
    _cachedRaw = raw;
    return true;
  } catch (e) {
    const isQuota = e instanceof DOMException && (
      e.code === 22 || e.code === 1014 || e.name === "QuotaExceededError" || e.name === "NS_ERROR_DOM_QUOTA_REACHED"
    );
    emitStorageError("write", isQuota
      ? "Storage quota exceeded. Please clear some browser data and try again."
      : "Failed to save basket. Your changes may not persist."
    );
    return false;
  }
}

function broadcastChange(items: BasketItem[]) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(BASKET_UPDATED_EVENT, {
      detail: { itemCount: items.length, items },
    }),
  );
}

type Listener = () => void;
const listeners = new Set<Listener>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) notifyListeners();
  });
  window.addEventListener(BASKET_UPDATED_EVENT, () => {
    notifyListeners();
  });
}

export const basketStore = {
  getItems(schoolId?: string): BasketItem[] {
    const items = readItems();
    if (!schoolId) return items;
    const cached = _cachedFiltered.get(schoolId);
    if (cached) return cached;
    const filtered = items.filter((i) => i.schoolId === schoolId);
    _cachedFiltered.set(schoolId, filtered);
    return filtered;
  },

  addItem(itemData: Omit<BasketItem, "id" | "addedAt">): BasketItem {
    const items = readItems();
    const newItem: BasketItem = {
      ...itemData,
      id: generateId(),
      addedAt: new Date().toISOString(),
    };
    const updated = [...items, newItem];
    const persisted = writeItems(updated);
    broadcastChange(updated);
    notifyListeners();
    if (!persisted) {
      emitStorageError("addItem", "Item was added to your in-memory basket but may not persist if you leave the page.");
    }
    return newItem;
  },

  removeItem(itemId: string) {
    const items = readItems();
    const updated = items.filter((i) => i.id !== itemId);
    writeItems(updated);
    broadcastChange(updated);
    notifyListeners();
  },

  /**
   * Stamp a `/widget/basket/validate` response onto the basket. THE write path
   * for validation state — every caller on this surface goes through it.
   *
   * Every field is written unconditionally, with no `available` /
   * `!available` branch. That is the whole point, and it is what the two
   * hand-rolled copies in the checkout overlay got wrong: a PARTIAL verdict
   * (`SESSION_PARTIAL_CAPACITY`, `PRODUCT_PARTIAL_STOCK`) comes back
   * `available: true` WITH a populated `unavailability` — deliberately, see
   * ValidateBasketItems, so the checkout button isn't blocked outright — and
   * both copies read it as "available", took the clean path, and hard-cleared
   * the verdict. "Only 2 spots remaining" was erased on arrival, so the drawer
   * never showed it and the buyer carried on with 5.
   *
   * Mirrors the widget's `updateValidationResults`
   * (apps/widget/src/stores/basket/index.ts) field for field, minus the one
   * block that has no meaning here: the balance/authoritative-price case (this
   * store has no `isBalancePayment` / `priceIsAuthoritative` — those are the
   * dashboard payment-link flows). The product postage/gifting surcharges are
   * stamped as the widget does since the product web component sells
   * products here (`buildValidatePayload` sends `postageId` / `isGift`).
   *
   * One write, one broadcast, one notify for the whole response — not one per
   * item, which is what calling `updateItem` in a loop did.
   */
  applyValidationResults(results: BasketValidationResult[]) {
    const items = readItems();
    const byId = new Map(results.map((r) => [r.itemId, r]));
    let matched = false;

    const updated = items.map((item) => {
      const result = byId.get(item.id);
      if (!result) return item;
      matched = true;

      // Whether this response is entitled to CLEAR the fields it does not
      // mention, as opposed to simply not knowing them.
      //
      // A clean result computed everything, so an absent `isDeposit` means
      // "not a deposit" and must erase a stale stamp. A result carrying a
      // verdict may have returned early through the server's `unavailable()`
      // helper, which stamps the refusal and nothing else — there an absent
      // field means "never looked", and erasing on it is destructive. Two
      // verdicts leave the item payable (`VALIDATION_TRANSIENT`, and the two
      // partials), so the buyer carries the damage to checkout: a shipped
      // product with no delivery-address form, a deposit ticket quoting the
      // full price, and a discount allocated against the wrong base.
      //
      // Note this is per FIELD, not per response — an earlier draft skipped
      // the whole write whenever a verdict was present, which was equally
      // wrong in the other direction. Verdict-bearing responses are not
      // uniformly empty: `validateProductItem` sends `requiresDeliveryAddress`
      // and the surcharges alongside PRODUCT_PARTIAL_STOCK, and the
      // aggregate-capacity pass stamps its verdict onto an already-complete
      // result. Whatever the response does carry still has to win.
      const mayClearUnmentioned = !result.unavailability;
      const keep = <T>(sent: T | undefined, held: T | undefined) =>
        sent !== undefined ? sent : mayClearUnmentioned ? undefined : held;

      // Resolved before the price, which has to read it. For a deposit item
      // `currentPrice` is not an override at all — it is the deposit LEG
      // ("already = deposit-due-now for deposit items",
      // ValidateBasketItems.service.ts:132), so it belongs to this block and
      // must not be decided independently of it.
      const nextIsDeposit = keep(
        result.isDeposit ? true : undefined,
        item.isDeposit,
      );

      // `currentPrice` is the one field `keep` must NOT decide, because it is
      // the one with a display-versus-CHARGE consequence.
      //
      // The provenance of a price CANNOT be recovered. When the server's
      // figure equals `item.price`, "it priced the item and got your number"
      // and "it echoed your number without pricing it" arrive as the same
      // integer, and no field records which. The unavailability CODE does not
      // separate them either: the per-item capacity path returns
      // SESSION_PARTIAL_CAPACITY *above* the `calculateBookingPrice` call
      // (ValidateBasketItems.service.ts:1636 vs :1645), while the aggregate
      // pass stamps the SAME code onto a fully-priced row (:1098-1115).
      // MEMBERS_ONLY, TAG_GATED, RESOURCE_POOL_SOLD_OUT and
      // TICKET_CONSTRAINT_VIOLATION are ambiguous the same way — and the
      // converse fails too, since `calculateBookingPrice` falls through to
      // `item.price` on four paths (:2127, :2138, :2294, :2339) that return
      // CLEAN results. A code allowlist is what failed the second attempt at
      // this guard; do not reach for one again.
      //
      // So the tie is broken by DIRECTION, not provenance. Displaying LESS
      // than is charged reaches a card statement; displaying more does not.
      // Holding only when the held figure is HIGHER is the unique rule with no
      // under-display branch.
      const nextCurrentPrice = ((): number | undefined => {
        const sent = result.currentPrice;
        // No figure at all — fall back to the same rule as its neighbours.
        if (sent === undefined) {
          return mayClearUnmentioned ? undefined : item.currentPrice;
        }
        // The server named a DIFFERENT number, so it priced the item. Take it.
        if (sent !== item.price) return sent;
        // Equal, and nothing was refused: it priced and found no override.
        if (mayClearUnmentioned) return undefined;
        // Equal, verdict-bearing, and this item is still a deposit whose split
        // this response did not restate: the figure is the deposit leg, so it
        // stays with the block `keep` is preserving. Clearing it alone leaves
        // ticket rows summing to the deposit under a full-contract total.
        if (nextIsDeposit && result.isDeposit === undefined) {
          return item.currentPrice;
        }
        // Equal, verdict-bearing, provenance unknowable. Hold only upward.
        return item.currentPrice !== undefined && item.currentPrice > item.price
          ? item.currentPrice
          : undefined;
      })();

      return {
        ...item,
        isAvailable: result.available,
        // A partial verdict is available AND carries a reason, so keying the
        // message on `!available` alone would drop exactly the text worth
        // showing ("Only 2 spots remaining").
        availabilityMessage:
          result.unavailability || !result.available
            ? result.message
            : undefined,
        unavailability: result.unavailability,
        // Derived, never decided separately: basket-item-card paints a warning
        // border on `priceChanged` ALONE, so a preserved `true` over a cleared
        // price would draw a warning that explains nothing.
        priceChanged:
          nextCurrentPrice !== undefined && nextCurrentPrice !== item.price,
        currentPrice: nextCurrentPrice,
        // Deposit split for the discount memo. Cleared when a CLEAN result
        // says the item is no longer priced as a deposit; preserved when a
        // verdict-bearing one simply did not compute it.
        isDeposit: keep(result.isDeposit ? true : undefined, item.isDeposit),
        depositAmount: keep(
          result.isDeposit ? result.depositAmount : undefined,
          item.depositAmount,
        ),
        remainingBalance: keep(
          result.isDeposit ? result.remainingBalance : undefined,
          item.remainingBalance,
        ),
        ticketDeposits: keep(
          result.isDeposit ? result.ticketDeposits : undefined,
          item.ticketDeposits,
        ),
        // Add-on total for the discount memo's add-on line (DEV-327); kept
        // with the price it belongs to.
        addOnTotalPence: keep(result.addOnTotalPence, item.addOnTotalPence),
        // Server truth for "this product ships", which drives the overlay's
        // delivery-address form. PRODUCT_PARTIAL_STOCK carries it, and that
        // item stays payable — so losing it here means a physical product
        // checked out with no address collected.
        requiresDeliveryAddress: keep(
          result.requiresDeliveryAddress ? true : undefined,
          item.requiresDeliveryAddress,
        ),
        // Server truth for the product surcharges, as the widget stamps them
        // (apps/widget/src/stores/basket/index.ts). The displayed total adds
        // these and the PaymentIntent adds exactly the same figures. A clean
        // result replaces them wholesale — no `?? item.x` fallback, or a
        // postage option the server refused would come back through the
        // `postagePrice × quantity` stand-in; a verdict-bearing result that
        // didn't restate them keeps what the item had.
        ...(item.type === "product"
          ? {
              postageName: keep(result.postage?.name, item.postageName),
              postagePrice: keep(result.postage?.unitPrice, item.postagePrice),
              postageTotal: mayClearUnmentioned
                ? (result.postageTotal ?? 0)
                : (result.postageTotal ?? item.postageTotal),
              giftingFee: mayClearUnmentioned
                ? (result.giftingFee ?? 0)
                : (result.giftingFee ?? item.giftingFee),
            }
          : {}),
      };
    });

    // A response naming none of our items changes nothing; writing anyway
    // would mint new identities and wake every subscriber for no reason.
    if (!matched) return;

    writeItems(updated);
    broadcastChange(updated);
    notifyListeners();
  },

  updateItem(itemId: string, updates: Partial<BasketItem>) {
    const items = readItems();
    const updated = items.map((i) =>
      i.id === itemId ? { ...i, ...updates } : i,
    );
    writeItems(updated);
    broadcastChange(updated);
    notifyListeners();
  },

  clearBasket(schoolId?: string) {
    if (schoolId) {
      const items = readItems();
      const updated = items.filter((i) => i.schoolId !== schoolId);
      writeItems(updated);
      broadcastChange(updated);
    } else {
      writeItems([]);
      broadcastChange([]);
    }
    notifyListeners();
  },

  getItemCount(schoolId?: string): number {
    // The shared count rule, so this, the basket button and Website v2's
    // navbar never disagree.
    return countBasketItems(this.getItems(schoolId));
  },

  getTotal(schoolId?: string): number {
    // `effectiveItemPrice` prefers the server-validated `currentPrice` (set by
    // the overlay's pre-checkout validate when it differs from the stamped
    // figure) so the displayed total matches what `CreateBasketCheckoutSession`
    // actually charges. Shared with the widget store to keep the rule in
    // lockstep. (Zero bundle cost: `@obby/lib` is already pulled in via
    // `@baluu/ui`'s basket components.)
    // Product surcharges (postage per unit, gift wrap per line) sit outside
    // the per-unit price and the PaymentIntent adds them the same way, so they
    // belong in the total — the same rule as the widget's getTotal.
    return this.getItems(schoolId).reduce(
      (sum, i) =>
        sum + effectiveItemPrice(i) * i.quantity + productSurcharge(i),
      0,
    );
  },

  /** See `_knownBuyerEmail`. Called by whichever surface learns it first. */
  setKnownBuyerEmail(email: string) {
    _knownBuyerEmail = email.trim() || null;
  },

  getKnownBuyerEmail(): string | undefined {
    return _knownBuyerEmail ?? undefined;
  },

  subscribe(callback: Listener) {
    listeners.add(callback);
    return () => listeners.delete(callback);
  },
};
